/**
 * useRemoteBrowser Hook
 *
 * React hook for managing remote browser sessions.
 * Handles connection, streaming, and input forwarding.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getRemoteBrowserClient,
  RemoteBrowserClient,
  ConnectionState,
  BrowserOpenedResponse,
  FramePayload,
} from '@shared/services/remoteBrowserClient';
import { logger } from '@shared/utils/logger';

// ============================================================================
// Types
// ============================================================================

/** Browser session state */
export interface BrowserSession {
  pageId: string;
  url: string;
  title: string;
  width: number;
  height: number;
  profileId: string;
  isLoading: boolean;
}

/** Selection result from getSelection */
export interface SelectionResult {
  text: string;
  success: boolean;
}

/** Hook return type */
export interface UseRemoteBrowserReturn {
  // State
  session: BrowserSession | null;
  connectionState: ConnectionState;
  error: string | null;
  currentFrame: string | null; // Base64 image
  isStreaming: boolean;

  // Actions
  openBrowser: (url: string, viewport?: { width: number; height: number }, profileId?: string) => Promise<void>;
  closeBrowser: () => void;
  navigate: (url: string) => Promise<void>;
  startStreaming: (options?: { fps?: number; quality?: number; useBinary?: boolean; adaptiveQuality?: boolean }) => void;
  stopStreaming: () => void;

  // Input forwarding
  sendMouseMove: (x: number, y: number) => void;
  sendMouseClick: (x: number, y: number, button?: 'left' | 'right' | 'middle') => void;
  sendScroll: (deltaX: number, deltaY: number) => void;
  sendKeyDown: (key: string, modifiers?: ('Alt' | 'Control' | 'Meta' | 'Shift')[]) => void;
  sendKeyUp: (key: string) => void;
  sendType: (text: string) => void;

  // Viewport control - call when panel size changes to sync Playwright viewport
  resizeViewport: (width: number, height: number, devicePixelRatio?: number) => void;

  // Clipboard & context menu actions
  getSelection: () => Promise<SelectionResult>;
  paste: (text: string) => void;
  reload: () => void;
  selectAll: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing remote browser sessions
 */
export function useRemoteBrowser(): UseRemoteBrowserReturn {
  const clientRef = useRef<RemoteBrowserClient | null>(null);

  // State
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [currentFrame, setCurrentFrame] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);

  // Ref to track current session pageId for frame handler (avoids stale closure)
  const sessionPageIdRef = useRef<string | null>(null);

  // Ref to track if browser is opening (prevents duplicate open calls)
  const isOpeningRef = useRef(false);

  // Ref to track streaming state (prevents cleanup from using stale state)
  const isStreamingRef = useRef(false);

  // Initialize client and connect
  useEffect(() => {
    const client = getRemoteBrowserClient();
    clientRef.current = client;

    // Connect with event handlers
    client.connect({
      onConnectionChange: (state) => {
        setConnectionState(state);
        if (state === 'disconnected') {
          setError('Connection lost. Reconnecting...');
        } else if (state === 'connected') {
          setError(null);
        }
      },
      onOpened: (data: BrowserOpenedResponse) => {
        // CRITICAL: Update ref IMMEDIATELY before state to avoid race condition
        // Frames may arrive before the useEffect that syncs the ref runs
        sessionPageIdRef.current = data.pageId;
        setSession({
          pageId: data.pageId,
          url: data.url,
          title: data.title,
          width: data.width,
          height: data.height,
          profileId: data.profileId,
          isLoading: false,
        });
        setError(null);
      },
      onNavigated: (data) => {
        setSession((prev) =>
          prev ? { ...prev, url: data.url, title: data.title, isLoading: false } : null
        );
      },
      onClosed: () => {
        // Clear ref immediately to stop accepting frames
        sessionPageIdRef.current = null;
        setSession(null);
        setCurrentFrame(null);
        setIsStreaming(false);
      },
      onError: (data) => {
        setError(data.error);
        logger.error('[useRemoteBrowser] Error:', data.error);
      },
      onFrame: (data: FramePayload) => {
        // Debug: log pageId mismatch to diagnose frame dropping
        if (sessionPageIdRef.current !== data.pageId) {
          // eslint-disable-next-line no-console
          console.warn(`[useRemoteBrowser] Frame pageId mismatch: ref=${sessionPageIdRef.current}, data=${data.pageId}`);
        }
        // Use ref to avoid stale closure - session state is captured at mount time
        if (sessionPageIdRef.current === data.pageId) {
          setCurrentFrame(data.image);
        }
      },
      onLoading: (data) => {
        setSession((prev) => (prev ? { ...prev, isLoading: data.isLoading } : null));
      },
      onTitle: (data) => {
        setSession((prev) => (prev ? { ...prev, title: data.title } : null));
      },
    });

    // Cleanup on unmount - use refs to avoid stale closure issues
    return () => {
      const pageId = sessionPageIdRef.current;
      if (pageId) {
        // Stop streaming first if active
        if (isStreamingRef.current) {
          client.stopStreaming(pageId);
        }
        client.closeBrowser(pageId);
      }
      // Don't disconnect the client since it's shared
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep sessionPageIdRef in sync with session state
  useEffect(() => {
    sessionPageIdRef.current = session?.pageId ?? null;
  }, [session?.pageId]);

  // Keep isStreamingRef in sync with isStreaming state
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Open a browser session
   * @param url - The URL to navigate to
   * @param viewport - Optional viewport dimensions
   * @param profileId - Optional profile ID for session persistence (e.g., domain-based)
   */
  const openBrowser = useCallback(
    async (url: string, viewport?: { width: number; height: number }, profileId?: string): Promise<void> => {
      const client = clientRef.current;
      if (!client) {
        setError('Client not initialized');
        return;
      }

      // Guard against duplicate open calls
      if (isOpeningRef.current || sessionPageIdRef.current) {
        logger.debug('[useRemoteBrowser] Skipping open - already opening or has session');
        return;
      }

      try {
        isOpeningRef.current = true;
        setError(null);
        const response = await client.openBrowser({ url, viewport, profileId });
        setSession({
          pageId: response.pageId,
          url: response.url,
          title: response.title,
          width: response.width,
          height: response.height,
          profileId: response.profileId,
          isLoading: false,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to open browser';
        setError(message);
        logger.error('[useRemoteBrowser] Open browser failed:', message);
      } finally {
        isOpeningRef.current = false;
      }
    },
    []
  );

  /**
   * Close the browser session
   * Uses ref to avoid dependency on session state (prevents re-creation)
   */
  const closeBrowser = useCallback((): void => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) return;

    // Stop streaming first if active
    if (isStreamingRef.current) {
      client.stopStreaming(pageId);
    }
    client.closeBrowser(pageId);
    setSession(null);
    setCurrentFrame(null);
    setIsStreaming(false);
  }, []); // No dependencies - uses refs

  /**
   * Navigate to a URL
   * Uses ref to avoid dependency on session state (prevents re-creation)
   */
  const navigate = useCallback(
    async (url: string): Promise<void> => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;

      try {
        setSession((prev) => (prev ? { ...prev, isLoading: true } : null));
        await client.navigate(pageId, url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Navigation failed';
        setError(message);
        setSession((prev) => (prev ? { ...prev, isLoading: false } : null));
      }
    },
    [] // No dependencies - uses refs
  );

  /**
   * Start screenshot streaming
   * Uses ref to avoid dependency on session state (prevents re-creation)
   */
  const startStreaming = useCallback(
    (options?: { fps?: number; quality?: number; useBinary?: boolean; adaptiveQuality?: boolean }): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;

      // Guard against starting if already streaming
      if (isStreamingRef.current) {
        logger.debug('[useRemoteBrowser] Already streaming, skipping start');
        return;
      }

      // Default to performance-optimized settings
      const streamOptions = {
        fps: options?.fps ?? 15,
        quality: options?.quality ?? 75,
        useBinary: options?.useBinary ?? true,
        adaptiveQuality: options?.adaptiveQuality ?? true,
        ...options,
      };

      client.startStreaming(pageId, streamOptions);
      setIsStreaming(true);
    },
    [] // No dependencies - uses refs
  );

  /**
   * Stop screenshot streaming
   * Uses ref to avoid dependency on session state (prevents re-creation)
   */
  const stopStreaming = useCallback((): void => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) return;

    client.stopStreaming(pageId);
    setIsStreaming(false);
  }, []); // No dependencies - uses refs

  // ==========================================================================
  // Input Forwarding
  // ==========================================================================

  const sendMouseMove = useCallback(
    (x: number, y: number): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.mouseMove(pageId, x, y);
    },
    [] // No dependencies - uses refs
  );

  const sendMouseClick = useCallback(
    (x: number, y: number, button: 'left' | 'right' | 'middle' = 'left'): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.mouseClick(pageId, x, y, button);
    },
    [] // No dependencies - uses refs
  );

  const sendScroll = useCallback(
    (deltaX: number, deltaY: number): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.mouseScroll(pageId, deltaX, deltaY);
    },
    [] // No dependencies - uses refs
  );

  const sendKeyDown = useCallback(
    (key: string, modifiers?: ('Alt' | 'Control' | 'Meta' | 'Shift')[]): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.keyDown(pageId, key, modifiers);
    },
    [] // No dependencies - uses refs
  );

  const sendKeyUp = useCallback(
    (key: string): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.keyUp(pageId, key);
    },
    [] // No dependencies - uses refs
  );

  const sendType = useCallback(
    (text: string): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.type(pageId, text);
    },
    [] // No dependencies - uses refs
  );

  /**
   * Resize viewport - call when panel size changes to sync Playwright viewport.
   * Width/height should be logical pixels matching the panel display size.
   * This ensures screenshots match the canvas size exactly (no stretching).
   */
  const resizeViewport = useCallback(
    (width: number, height: number, devicePixelRatio?: number): void => {
      const client = clientRef.current;
      const pageId = sessionPageIdRef.current;
      if (!client || !pageId) return;
      client.resizeViewport(pageId, width, height, devicePixelRatio);
    },
    [] // No dependencies - uses refs
  );

  // ==========================================================================
  // Clipboard & Context Menu
  // ==========================================================================

  /**
   * Get the currently selected text from the remote browser page.
   * Returns empty string if nothing is selected.
   */
  const getSelection = useCallback(async (): Promise<SelectionResult> => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) {
      return { text: '', success: false };
    }

    try {
      const result = await client.getSelection(pageId);
      return { text: result.text, success: result.success };
    } catch (err) {
      logger.error('[useRemoteBrowser] Get selection failed:', err);
      return { text: '', success: false };
    }
  }, []); // No dependencies - uses refs

  /**
   * Paste text at the current cursor position in the remote browser.
   */
  const paste = useCallback((text: string): void => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) return;
    client.paste(pageId, text);
  }, []); // No dependencies - uses refs

  /**
   * Reload the current page in the remote browser.
   */
  const reload = useCallback((): void => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) return;
    client.reload(pageId);
  }, []); // No dependencies - uses refs

  /**
   * Select all content on the page (Ctrl+A equivalent).
   */
  const selectAll = useCallback((): void => {
    const client = clientRef.current;
    const pageId = sessionPageIdRef.current;
    if (!client || !pageId) return;
    client.selectAll(pageId);
  }, []); // No dependencies - uses refs

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // State
    session,
    connectionState,
    error,
    currentFrame,
    isStreaming,

    // Actions
    openBrowser,
    closeBrowser,
    navigate,
    startStreaming,
    stopStreaming,

    // Input forwarding
    sendMouseMove,
    sendMouseClick,
    sendScroll,
    sendKeyDown,
    sendKeyUp,
    sendType,

    // Viewport control
    resizeViewport,

    // Clipboard & context menu
    getSelection,
    paste,
    reload,
    selectAll,
  };
}

export default useRemoteBrowser;
